import copy from '../../../copy/en/common.json' with { type: 'json' };
/**
 * Toasts (Radix Toast). `useToast().push({ title, tone })` from anywhere under
 * the provider; toasts auto-dismiss after 5s (8s for danger).
 */
import { Toast as RadixToast } from "radix-ui";
import { X } from "lucide-react";
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { Tone } from "../../lib/format.js";
import { cx } from "./cx.js";
import { IconButton } from "./Button.js";

export interface ToastInput {
  title: ReactNode;
  description?: ReactNode;
  tone?: Tone;
  durationMs?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

interface ToastContextValue {
  push: (t: ToastInput) => void;
  success: (title: ReactNode, description?: ReactNode) => void;
  error: (title: ReactNode, description?: ReactNode) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const push = useCallback((t: ToastInput) => {
    setItems((prev) => [...prev.slice(-4), { ...t, id: nextId++ }]);
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      push,
      success: (title, description) => push({ title, description, tone: "success" }),
      error: (title, description) => push({ title, description, tone: "danger" }),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={value}>
      <RadixToast.Provider swipeDirection="right" duration={5000}>
        {children}
        {items.map((t) => (
          <RadixToast.Root
            key={t.id}
            className={cx("toast", t.tone && t.tone !== "neutral" && `toast--${t.tone}`)}
            duration={t.durationMs ?? (t.tone === "danger" ? 8000 : 5000)}
            onOpenChange={(open) => {
              if (!open) setItems((prev) => prev.filter((x) => x.id !== t.id));
            }}
          >
            <div className="toast__body">
              <RadixToast.Title className="toast__title">{t.title}</RadixToast.Title>
              {t.description ? (
                <RadixToast.Description className="toast__description">{t.description}</RadixToast.Description>
              ) : null}
            </div>
            <RadixToast.Close asChild>
              <IconButton icon={X} label={copy.Toast.dismiss} size="sm" />
            </RadixToast.Close>
          </RadixToast.Root>
        ))}
        <RadixToast.Viewport className="toast__viewport" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
